import React, { useState, useEffect } from "react";
import { viewerDisabledProps, isViewer } from "../utils/permissions";

const API_BASE = "https://ims-attendanceback.indevconsultancy.in";

const GlobalHoliday = () => {
  const [holidays, setHolidays] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/global-holiday/`);
      const data = await res.json();
      if (data.status) setHolidays(data.holidays);
    } catch (err) {
      console.error("Failed to fetch holidays", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, []);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fromDate || !toDate) {
      showMessage("error", "Please select both From Date and To Date.");
      return;
    }
    if (new Date(toDate) < new Date(fromDate)) {
      showMessage("error", "To Date cannot be before From Date.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/global-holiday/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ from_date: fromDate, to_date: toDate, reason }),
      });
      const data = await res.json();
      if (data.status) {
        showMessage("success", "Holiday added successfully!");
        setFromDate("");
        setToDate("");
        setReason("");
        fetchHolidays();
      } else {
        showMessage("error", data.message || "Failed to add holiday.");
      }
    } catch (err) {
      showMessage("error", "Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to remove this holiday?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/global-holiday/${id}/delete/`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (data.status) {
        showMessage("success", "Holiday removed.");
        fetchHolidays();
      } else {
        showMessage("error", data.message || "Failed to remove holiday.");
      }
    } catch (err) {
      showMessage("error", "Network error. Please try again.");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const countDays = (from, to) => {
    const diff = (new Date(to) - new Date(from)) / (1000 * 60 * 60 * 24);
    return Math.round(diff) + 1;
  };

  return (
    <div className="container-fluid py-4 px-4">
      {/* Page Header */}
      <div className="d-flex align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-0" style={{ color: "#1a73e8" }}>
            Holidays
          </h4>
          {/* <p className="text-muted mb-0" style={{ fontSize: "0.875rem" }}>
            Declare paid holidays for all kitchens and all employees
          </p> */}
        </div>
      </div>

      {/* Alert Message */}
      {message && (
        <div
          className={`alert alert-${message.type === "success" ? "success" : "danger"} alert-dismissible`}
          role="alert"
          style={{ borderRadius: "8px" }}
        >
          {message.type === "success" ? "✅ " : "❌ "}
          {message.text}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage(null)}
          />
        </div>
      )}

      {/* Add Holiday Card */}
      <div className="card shadow-sm mb-4" style={{ borderRadius: "12px", border: "1px solid #e0e0e0" }}>
        <div
          className="card-header fw-bold"
          style={{
            background: "linear-gradient(135deg, #1a73e8, #0d47a1)",
            color: "#fff",
            borderRadius: "12px 12px 0 0",
            fontSize: "1rem",
          }}
        >
          + Add New Holiday
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3 align-items-end">
              {/* From Date */}
              <div className="col-12 col-md-3">
                <label className="form-label fw-semibold" style={{ fontSize: "0.85rem" }}>
                  From Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={{ borderRadius: "8px" }}
                  required
                />
              </div>

              {/* To Date */}
              <div className="col-12 col-md-3">
                <label className="form-label fw-semibold" style={{ fontSize: "0.85rem" }}>
                  To Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  min={fromDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={{ borderRadius: "8px" }}
                  required
                />
              </div>

              {/* Reason */}
              <div className="col-12 col-md-4">
                <label className="form-label fw-semibold" style={{ fontSize: "0.85rem" }}>
                  Reason / Holiday Name
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Diwali, Republic Day..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  style={{ borderRadius: "8px" }}
                />
              </div>

              {/* Submit Button */}
              <div className="col-12 col-md-2">
                <button
                  type="submit"
                  className="btn w-100 fw-bold"
                  disabled={submitting || isViewer()}
                  title={isViewer() ? "View-only role: action not allowed" : undefined}
                  style={{
                    background: "linear-gradient(135deg, #1a73e8, #0d47a1)",
                    color: "#fff",
                    borderRadius: "8px",
                    padding: "0.5rem 1rem",
                    border: "none",
                  }}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving...
                    </>
                  ) : (
                    "Add Holiday"
                  )}
                </button>
              </div>
            </div>

            {/* Live Preview */}
            {fromDate && toDate && new Date(toDate) >= new Date(fromDate) && (
              <div
                className="mt-3 p-3"
                style={{
                  background: "#e8f5e9",
                  borderRadius: "8px",
                  border: "1px solid #c8e6c9",
                  fontSize: "0.875rem",
                  color: "#2e7d32",
                }}
              >
                <strong>📋 Preview:</strong> Holiday from{" "}
                <strong>{formatDate(fromDate)}</strong> to{" "}
                <strong>{formatDate(toDate)}</strong> —{" "}
                <strong>{countDays(fromDate, toDate)} day(s)</strong> will be marked as
                paid holiday (H) for all kitchens and employees.
                {reason && <> &nbsp;| Reason: <strong>{reason}</strong></>}
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Info Banner */}
      {/* <div
        className="mb-4 p-3"
        style={{
          background: "#fff8e1",
          borderLeft: "4px solid #ffc107",
          borderRadius: "8px",
          fontSize: "0.85rem",
          color: "#5d4037",
        }}
      >
        <strong>ℹ️ How it works:</strong> Days marked as Global Holiday will be shown as{" "}
        <span
          className="badge"
          style={{ background: "#43a047", color: "#fff", borderRadius: "4px" }}
        >
          H
        </span>{" "}
        in attendance for <strong>all employees across all kitchens</strong>. These days
        are counted in <strong>Total Working Days</strong> and <strong>Payable Days</strong>,
        so salary is paid. Leave balance is <strong>not deducted</strong>.
      </div> */}

      {/* Holidays Table */}
      <div className="card shadow-sm" style={{ borderRadius: "12px", border: "1px solid #e0e0e0" }}>
        <div
          className="card-header fw-bold"
          style={{
            background: "#f5f5f5",
            borderRadius: "12px 12px 0 0",
            fontSize: "1rem",
            color: "#333",
          }}
        >
          📅 Declared Holidays ({holidays.length})
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" />
              <p className="mt-2 text-muted">Loading holidays...</p>
            </div>
          ) : holidays.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <div style={{ fontSize: "3rem" }}>🗓️</div>
              <p className="mt-2">No global holidays declared yet.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0" style={{ fontSize: "0.9rem" }}>
                <thead style={{ background: "#1a73e8", color: "#fff" }}>
                  <tr>
                    <th className="text-center py-3">#</th>
                    <th className="py-3">From Date</th>
                    <th className="py-3">To Date</th>
                    <th className="text-center py-3">Total Days</th>
                    <th className="py-3">Reason</th>
                    <th className="text-center py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {holidays.map((h, idx) => (
                    <tr key={h.id}>
                      <td className="text-center align-middle">{idx + 1}</td>
                      <td className="align-middle fw-semibold">{formatDate(h.from_date)}</td>
                      <td className="align-middle fw-semibold">{formatDate(h.to_date)}</td>
                      <td className="text-center align-middle">
                        <span
                          className="badge"
                          style={{
                            background: "#1a73e8",
                            color: "#fff",
                            borderRadius: "6px",
                            padding: "4px 10px",
                          }}
                        >
                          {countDays(h.from_date, h.to_date)} day(s)
                        </span>
                      </td>
                      <td className="align-middle">
                        {h.reason ? (
                          <span
                            className="badge"
                            style={{
                              background: "#e8f5e9",
                              color: "#2e7d32",
                              borderRadius: "6px",
                              padding: "4px 10px",
                              fontWeight: 500,
                            }}
                          >
                            {h.reason}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-center align-middle">
                        <button
                          className="btn btn-sm btn-danger"
                          style={{ borderRadius: "6px", fontSize: "0.8rem" }}
                          onClick={() => handleDelete(h.id)}
                          {...viewerDisabledProps()}
                        >
                          🗑 Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalHoliday;